/**
  * @class melePdf
  * @description
  *
  * # Purpose
  * Implements `<mele-pdf>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-pdf></mele-pdf>'
  * ```
  * Create element in JavaScript:
  * ```
  * parentEl.appendChildren({
  *   tag: 'mele-pdf',
  *   uri: 'path/to/file.pdf',
  * })
  * ```
  * Or pass file data directly:
  * ```
  * parentEl.appendChildren({
  *   tag: 'mele-pdf',
  *   file: arrayBufferOrUint8Array,
  * })
  * ```
  *
  * # Implementation notes
  *
  */

import meleElement from 'meleElement'

const PROPERTIES = _.extend({}, {
  uri: { type: 'string', description: 'URI of the PDF file to display' },
  file: { type: 'object', description: 'PDF file data as ArrayBuffer or Uint8Array' },
}, meleElement._properties)

export default class melePdf extends meleElement {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
  */
  init() {
    super.init()

    this._wrapper = this.appendChildren({
      tag: 'div',
      class: 'wrapper',
      onclick: this.previewPdf.bind(this),
    })

    this.elFrame = this._wrapper.appendChildren({
      tag: 'iframe',
      class: 'preview',
    })

    this._currentUrl = null
  } // init

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "uri"
  */
  uriChanged(value) {
    if(!this.elFrame || !value) return
    this._revokeCurrentUrl()

    // Show only first page in preview by adding #page=1
    const separator = value.includes('?') ? '&' : '#'
    this.elFrame.src = `${value}${separator}page=1`
    this._sourceUri = value
  } // uriChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of property "file"
  */
  fileChanged(value) {
    if(!this.elFrame || !value) return
    this._revokeCurrentUrl()

    // Convert file data to Blob and create object URL
    const blob = new Blob([value], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)

    // Show only first page in preview
    this.elFrame.src = `${url}#page=1`
    this._currentUrl = url
    this._sourceUri = url
  } // fileChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Revoke current object URL to prevent memory leaks
  */
  _revokeCurrentUrl() {
    if(this._currentUrl) {
      URL.revokeObjectURL(this._currentUrl)
      this._currentUrl = null
    }
  } // _revokeCurrentUrl

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Open PDF in fullscreen modal
  */
  previewPdf() {
    if(!this._sourceUri) return

    this.modalOpen({
      key: 'pdfpreview',
      position: 'full',
      items: [
        {
          tag: 'iframe',
          class: 'iframe',
          src: this._sourceUri, // Full PDF without page restriction
        },
      ],
      footerbuttons: ['cancel'],
    })
  } // previewPdf

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Cleanup when element is removed
  */
  disconnectedCallback() {
    super.disconnectedCallback?.()
    this._revokeCurrentUrl()
  } // disconnectedCallback
} // class melePdf

melePdf._properties = PROPERTIES
melePdf.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-pdf', melePdf)
